import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Person } from 'src/app/people/models/person';
import { PersonForMemberRequests } from 'src/app/people/models/personForApplication';
import { EmailPreference } from 'src/app/shared/models/emailPreference';
import { AppConfigService } from 'src/app/shared/services/app-config.service';
import { EmailPreferencesService } from 'src/app/shared/services/email-preferences.service';
import { MembersService } from 'src/app/shared/services/members.service';

@Component({
  selector: 'app-email-preferences',
  templateUrl: './email-preferences.component.html',
  styleUrls: ['./email-preferences.component.scss'],
  standalone: false
})
export class EmailPreferencesComponent implements OnInit, OnDestroy {
  isPersonLoaded: boolean;
  areEmailPrefsLoaded: boolean;
  displayOptedIn: boolean;

  private person?: PersonForMemberRequests;
  private commercialPreference?: EmailPreference;
  private readonly destroyed = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private appConfigService: AppConfigService,
    private membersService: MembersService,
    private emailPreferencesService: EmailPreferencesService
  ) {}

  async ngOnInit(): Promise<void> {
    const personId = this.getPersonId();
    const personPromise = this.membersService.getPerson(personId);

    this.emailPreferencesService
      .getCommercialEmailPreference(personId)
      .pipe(takeUntil(this.destroyed))
      .subscribe((emailPref) => {
        this.commercialPreference = emailPref;
        this.displayOptedIn = this.commercialPreference.isOptedIn;
        this.areEmailPrefsLoaded = true;
      });
    this.person = await personPromise;

    this.isPersonLoaded = true;
  }

  get optedInTitle(): string {
    return `Email preferences for ${this.person.email}`;
  }

  get optedOutTitle(): string {
    return `${this.person.email} is now unsubscribed`;
  }

  get tenantName(): string {
    return this.appConfigService.tenant.name;
  }

  private getPersonId(): string {
    return this.route.snapshot.paramMap.get('personId');
  }

  unsubscribe(): void {
    this.commercialPreference.isOptedIn = false;
    this.submit();
  }

  resubscribe(): void {
    this.commercialPreference.isOptedIn = true;
    this.submit();
  }

  private submit(): void {
    const personId = this.getPersonId();
    this.emailPreferencesService
      .updateEmailPreference(
        personId,
        this.commercialPreference.emailType,
        this.commercialPreference.toEmailPreferenceWrite()
      )
      .subscribe((_) => {
        this.displayOptedIn = this.commercialPreference.isOptedIn;
        this.emailPreferencesService.openSnackBar(
          'Your email preferences have been updated.',
          true
        );
        this.commercialPreference.markClean();
      });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
